import React, { useMemo, useCallback } from 'react';
import { Tooltip, TOOLTIP_POSITION } from 'taboola-ultimate-ui';
import { CONVERSION_LOCATION, CROSS_SITE_BUTTON_TYPE } from 'modules/codeless-events/consts/conversionFlowConstants';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './setupClicksEventCategory.module.scss';

export const MultiButtonMessage = ({ buttonNames, locationScope, crossSiteButtonType, trackedElements }) => {
    const { formatMessage } = useIntl();

    const isUrlMode =
        locationScope === CONVERSION_LOCATION.CROSS_SITE &&
        crossSiteButtonType === CROSS_SITE_BUTTON_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL;

    const displayData = useMemo(() => {
        if (isUrlMode && trackedElements && trackedElements.length > 0) {
            return trackedElements.map(element => element.href).filter(Boolean);
        }
        return buttonNames || [];
    }, [isUrlMode, trackedElements, buttonNames]);

    const getSuffixMessage = useCallback(() => {
        if (locationScope === CONVERSION_LOCATION.CROSS_SITE) {
            if (crossSiteButtonType === CROSS_SITE_BUTTON_TYPE.COMBINE_BUTTONS_THAT_CONTAINS_URL) {
                return formatMessage({
                    id: 'codeless.multi.button.message.suffix.url',
                    defaultMessage: ' - Button URL was selected',
                });
            }
            return formatMessage({
                id: 'codeless.multi.button.message.suffix.text',
                defaultMessage: ' - Button text was selected',
            });
        }
        return formatMessage({
            id: 'codeless.multi.button.message.suffix.default',
            defaultMessage: ' - Button was selected',
        });
    }, [locationScope, crossSiteButtonType, formatMessage]);

    const formatDisplayText = useCallback(
        data => {
            if (!data || data.length === 0) return '';

            if (isUrlMode) {
                if (data.length === 1) {
                    return data[0];
                }
                return data.join(', ');
            } else {
                if (data.length === 1) {
                    return `"${data[0]}"`;
                }
                return data.map(name => `"${name}"`).join(', ');
            }
        },
        [isUrlMode]
    );

    const { displayText, isTextTruncated, tooltipContent } = useMemo(() => {
        const suffix = getSuffixMessage();

        const baseTwoLineCapacity = 95;

        const isCrossSiteFlow = locationScope === CONVERSION_LOCATION.CROSS_SITE;
        const safetyMargin = isCrossSiteFlow ? 10 : 5;

        const suffixReservedSpace = suffix.length + safetyMargin;

        const maxTotalChars = baseTwoLineCapacity - suffixReservedSpace;

        const fullText = formatDisplayText(displayData);
        const fullMessage = `${fullText}${suffix}`;

        if (fullMessage.length <= baseTwoLineCapacity) {
            return {
                displayText: fullText,
                isTextTruncated: false,
                tooltipContent: null,
            };
        }

        const availableChars = maxTotalChars - 3;

        if (availableChars > 0) {
            const truncatedText = fullText.substring(0, availableChars) + '...';
            return {
                displayText: truncatedText,
                isTextTruncated: true,
                tooltipContent: fullText,
            };
        }

        return {
            displayText: fullText,
            isTextTruncated: false,
            tooltipContent: null,
        };
    }, [displayData, formatDisplayText, getSuffixMessage, locationScope]);

    const suffix = getSuffixMessage();

    const indicationContent = (
        <div className={styles['multi-button-message']}>
            <strong className={styles['tooltip-wrapper']}>{displayText}</strong>
            <span className={styles['suffix']}>{suffix}</span>
        </div>
    );

    if (isTextTruncated && tooltipContent) {
        return (
            <Tooltip
                position={TOOLTIP_POSITION.RIGHT}
                content={<div className={styles['tooltip-content']}>{tooltipContent}</div>}
            >
                {indicationContent}
            </Tooltip>
        );
    }

    return indicationContent;
};
